export const emptyLegalDisclosureDraft = (): API.LegalDisclosureDraft => ({
  enabled: false,
  affectedDomains: [],
  operatorName: "",
  jurisdiction: "",
  registeredAddress: "",
  companyRegistration: {
    authority: "",
    label: "",
    number: "",
    verificationUrl: "",
  },
  regulatoryLicenses: [],
  statements: [{ locale: "en_US", value: "" }],
  documentBindings: {
    termsDocumentId: null,
    privacyDocumentId: null,
    riskWarningDocumentId: null,
    amlDocumentId: null,
  },
});

const isHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const containsMarkup = (value: string): boolean => /<[^>]+>/.test(value);
const einPattern = /^\d{2}-\d{7}$/;

const requireValue = (
  errors: string[],
  value: string | undefined,
  message: string
) => {
  if (!value?.trim()) {
    errors.push(message);
  }
};

export const validateLegalDisclosureDraft = (
  draft: API.LegalDisclosureDraft
): string[] => {
  if (!draft.enabled) {
    return [];
  }

  const errors: string[] = [];
  if (draft.affectedDomains.length === 0) {
    errors.push("至少配置一个适用域名");
  }
  requireValue(errors, draft.operatorName, "运营主体名称不能为空");
  requireValue(errors, draft.jurisdiction, "注册司法辖区不能为空");
  requireValue(errors, draft.registeredAddress, "注册地址不能为空");
  requireValue(
    errors,
    draft.companyRegistration.authority,
    "公司登记机构不能为空"
  );
  requireValue(
    errors,
    draft.companyRegistration.label,
    "公司登记编号名称不能为空"
  );
  requireValue(
    errors,
    draft.companyRegistration.number,
    "公司登记编号不能为空"
  );

  const registrationUrl = draft.companyRegistration.verificationUrl?.trim();
  if (registrationUrl && !isHttpUrl(registrationUrl)) {
    errors.push("公司登记核验链接必须是 http 或 https 地址");
  }

  const normalizedDomains = draft.affectedDomains.map((domain) =>
    domain.trim().toLowerCase()
  );
  if (normalizedDomains.some((domain) => !domain || /[/:\s]/.test(domain))) {
    errors.push("适用域名只能填写主机名，不能包含协议、端口、路径或空格");
  }
  if (new Set(normalizedDomains).size !== normalizedDomains.length) {
    errors.push("适用域名不能重复");
  }

  const normalizedLocales = draft.statements.map((statement) =>
    statement.locale.trim()
  );
  if (!normalizedLocales.includes("en_US")) {
    errors.push("至少配置一条 en_US 英文声明");
  }
  normalizedLocales.forEach((locale, index) => {
    if (!locale) {
      errors.push(`声明 ${index + 1} 的语言不能为空`);
    }
    if (!draft.statements[index].value.trim()) {
      errors.push(`声明 ${index + 1} 的内容不能为空`);
    }
  });
  const duplicateLocales = normalizedLocales.filter(
    (locale, index) => normalizedLocales.indexOf(locale) !== index
  );
  Array.from(new Set(duplicateLocales)).forEach((locale) => {
    errors.push(`声明语言不能重复：${locale}`);
  });
  if (draft.statements.some((statement) => containsMarkup(statement.value))) {
    errors.push("声明内容只能使用纯文本");
  }

  if (!draft.documentBindings.termsDocumentId) {
    errors.push("用户协议文档 ID 不能为空");
  }
  if (!draft.documentBindings.privacyDocumentId) {
    errors.push("隐私政策文档 ID 不能为空");
  }

  draft.regulatoryLicenses.forEach((license, index) => {
    const number = index + 1;
    requireValue(
      errors,
      license.authority,
      `牌照 ${number} 的监管机构不能为空`
    );
    requireValue(errors, license.label, `牌照 ${number} 的编号名称不能为空`);
    requireValue(errors, license.number, `牌照 ${number} 的编号不能为空`);
    requireValue(
      errors,
      license.jurisdiction,
      `牌照 ${number} 的司法辖区不能为空`
    );
    const url = license.verificationUrl?.trim();
    if (url && !isHttpUrl(url)) {
      errors.push(`牌照 ${number} 的核验链接必须是 http 或 https 地址`);
    }
    if (
      license.number.trim() &&
      license.number.trim() === draft.companyRegistration.number.trim()
    ) {
      errors.push(`牌照 ${number} 不能复用公司登记编号`);
    }
    if (
      /\bEIN\b/i.test(license.label) ||
      /Internal Revenue Service/i.test(license.authority) ||
      einPattern.test(license.number.trim())
    ) {
      errors.push(`牌照 ${number} 不能使用 EIN 税号`);
    }
  });

  return errors;
};
